import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const features = [
  {
    title: 'AI Training Centre',
    description: 'Train your nonprofit’s AI Assistant with your current assests and past work.',
    image: '/images/solutions/knowledge-hub/data-utilization.jpg',
    bullets: [
      'Secure AI Training Center – Upload reports, catalogs, brochures, and impact documents.',
      'Automated Data Segmentation – AI intelligently extracts and organizes key insights.',
      'Context Building for AI Growth Engine – Fuels AI-powered content generation with relevant insights.',
      'Smart AI Responses – Powers the AI Assistant to provide accurate answers based on uploaded data.'
    ]
  },
  {
    title: 'Seamless AI Integration for Maximum Impact',
    description: 'Leverage AI to enhance engagement, automate content creation, and provide data-driven responses.',
    image: '/images/solutions/knowledge-hub/ai-integration.jpg',
    bullets: [
      'Works with CauseConnect’s AI Growth Engine – Generate campaign content, proposals, and reports.',
      'Optimizes AI Assistant Responses – Visitors and corporate partners receive data-driven answers.',
      'Instant Retrieval – AI can pull insights from past records for immediate response.'
    ]
  },
];

export const KnowledgeHubPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-3xl font-bold mb-5">
              CauseConnect: Knowledge Hub
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-6">
              Your AI-Powered Nonprofit Data Center
            </p>
            <p className="text-lg text-gray-200">
              Centralize, analyze, and utilize your nonprofit’s data for smarter engagement. The Knowledge Hub securely stores and structures your documents, enabling AI-driven insights, automated content generation, and intelligent visitor interactions.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        {features.map((feature, index) => (
          <div 
            key={feature.title}
            className={`flex flex-col md:flex-row items-center gap-12 py-16 ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}
          >
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{feature.title}</h2>
              <p className="text-gray-600 mb-6">{feature.description}</p>
              <ul className="space-y-3">
                {feature.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1">
              <img
                src={feature.image}
                alt={feature.title}
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-[#2D7D46] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Leverage the Power of AI-Powered Knowledge?
          </h2>
          <p className="text-xl mb-8">
            Upload your documents and let AI structure, analyze, and enhance your nonprofit’s impact.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#2D7D46] hover:bg-gray-100"
            onClick={() => navigate('/signup')}
          >
            Experience AI-Powered Knowledge Hub
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeHubPage;
